/**
 * Palette de commandes (mandat §15) — `Ctrl+K` / `Cmd+K`.
 *
 * Une recherche qui est aussi un outil de NAVIGATION : résultats groupés par catégorie
 * (pages, incidents, sites), au clavier de bout en bout (flèches, entrée, échap), focus
 * piégé dans la fenêtre modale. Les résultats arrivent des sources déjà branchées
 * (`/recherche` pour les objets du référentiel) — aucune donnée inventée.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getList } from "@/api/client";
import { IconFermer, IconRecherche } from "./icons";

interface Resultat {
  categorie: string;
  label: string;
  detail?: string;
  to: string;
}

interface PageCmd {
  label: string;
  to: string;
  mots: string;
}

/** Les pages de la console : la palette doit aussi naviguer vers un écran. */
const PAGES: PageCmd[] = [
  { label: "Centre de commandement", to: "/", mots: "accueil dashboard kpi" },
  { label: "Carte du réseau", to: "/carte-v2", mots: "sig carte carte géographie" },
  { label: "Topologie du réseau", to: "/reseau", mots: "topologie graphe" },
  { label: "Carte des services", to: "/carte-services", mots: "service chaîne" },
  { label: "Intelligence", to: "/intelligence", mots: "risque capacité diagnostic" },
  { label: "Qualité des données", to: "/qualite-donnees", mots: "qualité constats" },
  { label: "Vue NOC", to: "/noc", mots: "supervision noc" },
  { label: "Incidents", to: "/incidents", mots: "incidents pannes" },
  { label: "Capacité", to: "/capacite", mots: "capacité saturation" },
  { label: "Carte d'urgence", to: "/urgence", mots: "urgence hors connexion" },
  { label: "Missions", to: "/mission", mots: "terrain missions" },
  { label: "Direction", to: "/direction", mots: "direction décision" },
  { label: "Rapports", to: "/rapports", mots: "rapports exports" },
  { label: "Importer des données", to: "/import", mots: "import csv" },
  { label: "Administration", to: "/administration", mots: "admin paramètres" },
];

export function CommandPalette({ ouvert, fermer }: { ouvert: boolean; fermer: () => void }) {
  const navigate = useNavigate();
  const [saisie, setSaisie] = useState("");
  const [resultatsObjets, setResultatsObjets] = useState<Resultat[] | null>(null);
  const [selection, setSelection] = useState(0);
  const champRef = useRef<HTMLInputElement>(null);
  const listeRef = useRef<HTMLUListElement>(null);

  // Pages filtrées localement — toujours disponibles, même hors ligne.
  const pagesFiltrees = useMemo(() => {
    const q = saisie.trim().toLowerCase();
    if (!q) return PAGES.slice(0, 5).map((p) => ({ categorie: "Pages", label: p.label, to: p.to }));
    return PAGES.filter((p) => `${p.label} ${p.mots}`.toLowerCase().includes(q))
      .slice(0, 6)
      .map((p) => ({ categorie: "Pages", label: p.label, to: p.to }));
  }, [saisie]);

  // Objets du référentiel : la recherche serveur, appelée avec un léger tempo.
  useEffect(() => {
    if (!ouvert) return undefined;
    const q = saisie.trim();
    if (q.length < 2) {
      setResultatsObjets(null);
      return undefined;
    }
    const tempo = setTimeout(async () => {
      try {
        const reponse = await getList<{ id: string; name: string; type?: string; code?: string }>(
          "/recherche/",
          { q },
        );
        setResultatsObjets(
          reponse.slice(0, 8).map((r) => ({
            categorie: "Objets du référentiel",
            label: r.name || r.code || r.id,
            detail: r.type,
            to: `/recherche?q=${encodeURIComponent(q)}`,
          })),
        );
      } catch {
        // La recherche serveur peut être interdite au rôle : la palette garde les pages,
        // et ne montre PAS une erreur pour une capacité optionnelle.
        setResultatsObjets(null);
      }
    }, 250);
    return () => clearTimeout(tempo);
  }, [saisie, ouvert]);

  const resultats = useMemo(() => {
    const groupes: Resultat[] = [...pagesFiltrees];
    if (resultatsObjets) groupes.push(...resultatsObjets);
    return groupes;
  }, [pagesFiltrees, resultatsObjets]);

  useEffect(() => {
    if (ouvert) {
      setSaisie("");
      setResultatsObjets(null);
      setSelection(0);
      // Le focus arrive après le montage : requestAnimationFrame évite le conflit avec
      // la transition d'apparition.
      requestAnimationFrame(() => champRef.current?.focus());
    }
  }, [ouvert]);

  const aller = useCallback(
    (index: number) => {
      const cible = resultats[index];
      if (!cible) return;
      fermer();
      navigate(cible.to);
    },
    [resultats, fermer, navigate],
  );

  if (!ouvert) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-nuit/60 p-4 pt-[12vh]"
      onClick={fermer}
      role="presentation"
    >
      {/* La fenêtre stoppe la propagation : cliquer dedans ne ferme pas. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Palette de commandes"
        className="w-full max-w-xl overflow-hidden rounded-lg border border-bordure bg-surface shadow-e3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-bordure px-3">
          <IconRecherche className="text-texte-2" />
          <input
            ref={champRef}
            value={saisie}
            onChange={(e) => {
              setSaisie(e.target.value);
              setSelection(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelection((s) => Math.min(s + 1, resultats.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelection((s) => Math.max(0, s - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                aller(selection);
              } else if (e.key === "Escape") {
                fermer();
              }
            }}
            placeholder="Rechercher un site, un incident, une page…"
            aria-label="Recherche globale"
            className="h-12 flex-1 bg-transparent text-sm text-texte placeholder:text-texte-2 focus-visible:outline-none"
          />
          <button
            type="button"
            onClick={fermer}
            aria-label="Fermer la palette"
            className="rounded p-1.5 text-texte-2 hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech-700"
          >
            <IconFermer size={16} />
          </button>
        </div>

        <ul ref={listeRef} className="max-h-80 overflow-y-auto py-1" role="listbox">
          {resultats.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-texte-2">
              Aucun résultat. Un objet non trouvé n'existe peut-être pas encore dans le
              référentiel.
            </li>
          )}
          {resultats.map((r, i) => (
            <li key={`${r.categorie}:${r.label}:${i}`} role="option" aria-selected={i === selection}>
              <button
                type="button"
                onMouseEnter={() => setSelection(i)}
                onClick={() => aller(i)}
                className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm ${
                  i === selection ? "bg-tech-100 text-texte" : "text-texte"
                } focus-visible:outline-none`}
              >
                <span>
                  {r.label}
                  {r.detail && <span className="ml-2 text-xs text-texte-2">{r.detail}</span>}
                </span>
                <span className="text-xs uppercase tracking-wide text-texte-2">{r.categorie}</span>
              </button>
            </li>
          ))}
        </ul>

        <footer className="flex items-center gap-3 border-t border-bordure px-4 py-1.5 text-xs text-texte-2">
          <span>↑↓ naviguer</span>
          <span>↵ ouvrir</span>
          <span>échap fermer</span>
        </footer>
      </div>
    </div>
  );
}
